import React, { useState, useEffect } from 'react';
import {
  Box,
  Chip,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Fade,
  Divider,
  CircularProgress,
  Alert
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import {
  buildLocationPath,
  parseLocationPath,
  getRecentlyUsedLocations,
  addToRecentlyUsedLocations,
  clearRecentlyUsedLocations,
  getSuggestedLocations
} from '../data/locationHierarchy';
import { colors } from '../styles/styleConstants';
import { directApi } from '../services/api';

// Maximum location depth supported
const MAX_LOCATION_DEPTH = 5;

/**
 * LocationHierarchySelector - Multi-level location selection with dynamic chip navigation
 */
const LocationHierarchySelector = ({ 
  value = '', 
  onChange, 
  onBlur,
  label = 'Location',
  placeholder = 'Select location...',
  disabled = false,
  showSuggestions = true,
  itemType = null,
  boxName = null
}) => {
  // State management
  const [locationPath, setLocationPath] = useState([]);
  const [currentLevel, setCurrentLevel] = useState(0);
  const [availableOptions, setAvailableOptions] = useState([]);
  const [isCustomDialogOpen, setIsCustomDialogOpen] = useState(false);
  const [customLocation, setCustomLocation] = useState('');
  const [customDialogType, setCustomDialogType] = useState('full'); // 'full' or 'contextual'
  const [customDialogContext, setCustomDialogContext] = useState('');
  const [recentlyUsed, setRecentlyUsed] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  
  // Database-driven location tree
  const [locationTree, setLocationTree] = useState(null);
  const [isLoadingTree, setIsLoadingTree] = useState(true);
  
  // Fetch location tree from API on mount
  useEffect(() => {
    const fetchLocationTree = async () => {
      try {
        setIsLoadingTree(true);
        // Use /location-paths endpoint which returns flat array of all locations
        const response = await directApi.get('/location-paths');
        if (response.data && response.data.locations) {
          // locations is a flat array with depth, path, name, icon
          setLocationTree(response.data.locations);
        } else if (Array.isArray(response.data)) {
          setLocationTree(response.data);
        } else {
          setLocationTree([]);
        }
      } catch (error) {
        console.error('Error fetching location tree:', error);
        // Fallback will use empty array
        setLocationTree([]);
      } finally {
        setIsLoadingTree(false);
      }
    };
    
    fetchLocationTree();
  }, []);

  // Initialize component with existing value
  useEffect(() => {
    if (value) {
      const parsedPath = parseLocationPath(value);
      setLocationPath(parsedPath);
      setCurrentLevel(parsedPath.length);
    } else {
      setLocationPath([]);
      setCurrentLevel(0);
    }
  }, [value]);

  // Update available options when level changes or tree loads
  useEffect(() => {
    updateAvailableOptions();
  }, [locationPath, currentLevel, locationTree, isLoadingTree]);

  // Load recently used and suggestions
  useEffect(() => {
    if (showSuggestions) {
      refreshRecentlyUsed();
      setSuggestions(getSuggestedLocations(itemType, boxName));
    }
  }, [showSuggestions, itemType, boxName]);

  // Function to refresh recently used locations
  const refreshRecentlyUsed = () => {
    setRecentlyUsed(getRecentlyUsedLocations());
  };

  // Function to clear recently used locations
  const handleClearRecentlyUsed = () => {
    clearRecentlyUsedLocations();
    refreshRecentlyUsed();
  };

  // Helper function to handle final location selection and update recent list
  const handleFinalLocationSelection = (locationString, locationPathArray = null) => {
    onChange(locationString);
    
    // Add to recently used locations
    const pathArray = locationPathArray || parseLocationPath(locationString);
    if (pathArray.length > 0) {
      addToRecentlyUsedLocations(pathArray);
      // Refresh the recently used list to show the update
      refreshRecentlyUsed();
    }
  };

  // Helper functions to navigate the location tree from API
  const getPropertiesFromTree = () => {
    if (!locationTree) return [];
    // Properties are top-level locations (depth 0)
    return locationTree.filter(loc => loc.depth === 0).map(loc => ({
      name: loc.name,
      icon: loc.icon || '📍',
      path: loc.path
    }));
  };
  
  const getCategoriesFromTree = (propertyName) => {
    if (!locationTree) return [];
    // Find the property and get its children (depth 1)
    const property = locationTree.find(loc => loc.depth === 0 && loc.name === propertyName);
    if (!property) return [];
    
    return locationTree.filter(loc => 
      loc.depth === 1 && loc.path.startsWith(property.path + '/')
    ).map(loc => ({
      name: loc.name,
      icon: loc.icon || '📍',
      path: loc.path
    }));
  };
  
  const getSubcategoriesFromTree = (propertyName, categoryName) => {
    if (!locationTree) return [];
    const parentPath = `/${propertyName}/${categoryName}`;
    
    return locationTree.filter(loc => 
      loc.depth === 2 && loc.path.startsWith(parentPath + '/')
    ).map(loc => ({
      name: loc.name,
      icon: loc.icon || '📍',
      path: loc.path
    }));
  };
  
  const getSubSubcategoriesFromTree = (propertyName, categoryName, subcategoryName) => {
    if (!locationTree) return [];
    const parentPath = `/${propertyName}/${categoryName}/${subcategoryName}`;
    
    return locationTree.filter(loc => 
      loc.depth === 3 && loc.path.startsWith(parentPath + '/')
    ).map(loc => ({
      name: loc.name,
      icon: loc.icon || '📍',
      path: loc.path
    }));
  };
  
  const getLevel4FromTree = (propertyName, categoryName, subcategoryName, subSubcategoryName) => {
    if (!locationTree) return [];
    const parentPath = `/${propertyName}/${categoryName}/${subcategoryName}/${subSubcategoryName}`;
    
    return locationTree.filter(loc => 
      loc.depth === 4 && loc.path.startsWith(parentPath + '/')
    ).map(loc => ({
      name: loc.name,
      icon: loc.icon || '📍',
      path: loc.path
    }));
  };

  const updateAvailableOptions = () => {
    if (!locationTree || isLoadingTree) return;
    
    let options = [];

    if (currentLevel === 0) {
      // Show properties (top level)
      options = getPropertiesFromTree();
    } else if (currentLevel === 1) {
      // Show categories for selected property
      options = getCategoriesFromTree(locationPath[0]);
    } else if (currentLevel === 2) {
      // Show subcategories
      options = getSubcategoriesFromTree(locationPath[0], locationPath[1]);
    } else if (currentLevel === 3) {
      // Show sub-subcategories (level 4)
      options = getSubSubcategoriesFromTree(locationPath[0], locationPath[1], locationPath[2]);
    } else if (currentLevel === 4) {
      // Show level 5 options
      options = getLevel4FromTree(locationPath[0], locationPath[1], locationPath[2], locationPath[3]);
    }

    setAvailableOptions(options);
  };

  const handleOptionSelect = (option) => {
    if (disabled) return;

    // option can be a string or an object with name property
    const optionName = typeof option === 'object' ? option.name : option;
    
    const newPath = [...locationPath];
    newPath[currentLevel] = optionName;
    
    // Trim path to current level + 1
    const trimmedPath = newPath.slice(0, currentLevel + 1);
    
    setLocationPath(trimmedPath);
    
    // Check if we can go deeper
    const canGoDeeper = checkCanGoDeeper(trimmedPath);
    
    if (canGoDeeper) {
      // Move to next level
      setCurrentLevel(currentLevel + 1);
    } else {
      // This is the final selection
      const finalLocation = buildLocationPath(trimmedPath);
      handleFinalLocationSelection(finalLocation, trimmedPath);
      setCurrentLevel(trimmedPath.length);
    }
  };

  const checkCanGoDeeper = (path) => {
    if (!locationTree) return false;
    
    // Cannot go deeper than max depth
    if (path.length >= MAX_LOCATION_DEPTH) return false;
    
    if (path.length === 1) {
      // Check if property has categories
      return getCategoriesFromTree(path[0]).length > 0;
    } else if (path.length === 2) {
      // Check if category has subcategories
      return getSubcategoriesFromTree(path[0], path[1]).length > 0;
    } else if (path.length === 3) {
      // Check if subcategory has sub-subcategories
      return getSubSubcategoriesFromTree(path[0], path[1], path[2]).length > 0;
    } else if (path.length === 4) {
      // Check if level 4 has level 5 options
      return getLevel4FromTree(path[0], path[1], path[2], path[3]).length > 0;
    }
    return false;
  };

  const handleBackNavigation = () => {
    if (currentLevel > 0) {
      setCurrentLevel(currentLevel - 1);
      // Remove the last level from path
      const newPath = locationPath.slice(0, currentLevel - 1);
      setLocationPath(newPath);
      
      // Update the value
      const newLocation = buildLocationPath(newPath);
      onChange(newLocation);
    }
  };

  const handleBreadcrumbClick = (level) => {
    if (disabled) return;
    
    setCurrentLevel(level + 1);
    const newPath = locationPath.slice(0, level + 1);
    setLocationPath(newPath);
    
    const newLocation = buildLocationPath(newPath);
    onChange(newLocation);
  };

  const handleQuickSelect = (path) => {
    if (disabled) return;
    
    setLocationPath(path);
    setCurrentLevel(path.length);
    const location = buildLocationPath(path);
    handleFinalLocationSelection(location, path);
  };

  const handleCustomLocation = (type = 'full') => {
    setCustomDialogType(type);
    
    if (type === 'contextual') {
      // Build context string for the dialog
      const contextParts = [];
      if (currentLevel === 1 && locationPath[0]) {
        contextParts.push(locationPath[0]);
      } else if (currentLevel === 2 && locationPath[0] && locationPath[1]) {
        contextParts.push(locationPath[0], locationPath[1]);
      }
      setCustomDialogContext(contextParts.join(' - '));
    } else {
      setCustomDialogContext('');
    }
    
    setIsCustomDialogOpen(true);
  };

  const handleCustomSubmit = async () => {
    if (customLocation.trim()) {
      let finalLocation;
      let parentPath = null;
      
      if (customDialogType === 'contextual') {
        // Add the custom option to the current context
        const newPath = [...locationPath];
        newPath[currentLevel] = customLocation.trim();
        
        // Build the parent path for the API call
        if (currentLevel > 0) {
          parentPath = '/' + locationPath.slice(0, currentLevel).join('/');
        }
        
        // Build the full location path
        finalLocation = buildLocationPath(newPath.slice(0, currentLevel + 1));
        const finalPath = newPath.slice(0, currentLevel + 1);
        
        // Save custom location to database
        try {
          await directApi.post('/location-paths', {
            name: customLocation.trim(),
            parent_path: parentPath,
            icon: '📍'
          });
          console.log('Custom location saved to database:', customLocation.trim());
          
          // Refresh the location tree to include the new location
          const response = await directApi.get('/location-paths');
          if (response.data && response.data.locations) {
            setLocationTree(response.data.locations);
          }
        } catch (error) {
          console.error('Error saving custom location:', error);
          // Continue anyway - the location will still work for this box
        }
        
        // Update the path and level
        setLocationPath(finalPath);
        setCurrentLevel(currentLevel + 1);
        
        // Use helper to handle final selection and update recent list
        handleFinalLocationSelection(finalLocation, finalPath);
      } else {
        // Full custom location - parse and save each level
        finalLocation = customLocation.trim();
        const parsedPath = parseLocationPath(customLocation.trim());
        
        // Save each level of the custom location to database
        try {
          let currentParentPath = null;
          for (let i = 0; i < parsedPath.length; i++) {
            await directApi.post('/location-paths', {
              name: parsedPath[i],
              parent_path: currentParentPath,
              icon: '📍'
            });
            currentParentPath = currentParentPath ? `${currentParentPath}/${parsedPath[i]}` : `/${parsedPath[i]}`;
          }
          console.log('Full custom location saved to database:', finalLocation);
          
          // Refresh the location tree
          const response = await directApi.get('/location-paths');
          if (response.data && response.data.locations) {
            setLocationTree(response.data.locations);
          }
        } catch (error) {
          console.error('Error saving custom location:', error);
        }
        
        setLocationPath(parsedPath);
        setCurrentLevel(parsedPath.length);
        
        // Use helper to handle final selection and update recent list
        handleFinalLocationSelection(finalLocation, parsedPath);
      }
    }
    
    // Reset dialog state
    setIsCustomDialogOpen(false);
    setCustomLocation('');
    setCustomDialogType('full');
    setCustomDialogContext('');
  };

  const handleFinishSelection = () => {
    const finalLocation = buildLocationPath(locationPath);
    handleFinalLocationSelection(finalLocation, locationPath);
  };

  const handleDialogClose = () => {
    setIsCustomDialogOpen(false);
    setCustomLocation('');
    setCustomDialogType('full');
    setCustomDialogContext('');
  };

  const renderBreadcrumb = () => {
    if (locationPath.length === 0) return null;

    return (
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
          Path:
        </Typography>
        {locationPath.map((step, index) => (
          <React.Fragment key={index}>
            <Chip
              label={step}
              variant="filled"
              size="small"
              onClick={() => handleBreadcrumbClick(index)}
              sx={{
                backgroundColor: colors.actionBlue,
                color: 'white',
                cursor: disabled ? 'default' : 'pointer',
                '&:hover': disabled ? {} : {
                  backgroundColor: colors.darkSlateBlue,
                }
              }}
            />
            {index < locationPath.length - 1 && (
              <ArrowForwardIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            )}
          </React.Fragment>
        ))}
      </Box>
    );
  };

  const renderQuickSelections = () => {
    if (!showSuggestions || (recentlyUsed.length === 0 && suggestions.length === 0)) return null;

    return (
      <Box sx={{ mb: 2 }}>
        {recentlyUsed.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Recently Used:
              </Typography>
              <Button
                size="small"
                startIcon={<ClearIcon />}
                onClick={handleClearRecentlyUsed}
                sx={{
                  fontSize: '0.7rem',
                  minWidth: 'auto',
                  px: 1,
                  py: 0.25,
                  color: colors.mediumSlateBlue,
                  '&:hover': {
                    backgroundColor: colors.lightGray,
                  }
                }}
              >
                Clear
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {recentlyUsed.slice(0, 5).map((path, index) => (
                <Chip
                  key={index}
                  label={buildLocationPath(path)}
                  variant="outlined"
                  size="small"
                  onClick={() => handleQuickSelect(path)}
                  sx={{
                    fontSize: '0.75rem',
                    cursor: disabled ? 'default' : 'pointer',
                    '&:hover': disabled ? {} : {
                      backgroundColor: colors.lightGray,
                    }
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {suggestions.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Suggested:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {suggestions.slice(0, 3).map((path, index) => (
                <Chip
                  key={index}
                  label={buildLocationPath(path)}
                  variant="outlined"
                  size="small"
                  onClick={() => handleQuickSelect(path)}
                  sx={{
                    fontSize: '0.75rem',
                    cursor: disabled ? 'default' : 'pointer',
                    borderColor: colors.actionBlue,
                    color: colors.actionBlue,
                    '&:hover': disabled ? {} : {
                      backgroundColor: colors.actionBlue,
                      color: 'white',
                    }
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
        <Divider sx={{ my: 1 }} />
      </Box>
    );
  };

  const renderCurrentLevelOptions = () => {
    const levelNames = ['Property', 'Category', 'Area', 'Specific Location', 'Detail'];
    const levelName = levelNames[currentLevel] || 'Location';
    
    // Check if we're at max depth
    const isAtMaxDepth = currentLevel >= MAX_LOCATION_DEPTH;

    // Show loading state while fetching tree
    if (isLoadingTree) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Loading locations...
          </Typography>
        </Box>
      );
    }

    return (
      <Box>
        {/* Show warning if at max depth */}
        {isAtMaxDepth && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Maximum location depth ({MAX_LOCATION_DEPTH} levels) reached. You can use this location or go back to select a different path.
          </Alert>
        )}
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Select {levelName}:
        </Typography>
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {/* Back button */}
          {currentLevel > 0 && (
            <Chip
              icon={<ArrowBackIcon />}
              label="Back"
              variant="outlined"
              onClick={handleBackNavigation}
              sx={{
                cursor: disabled ? 'default' : 'pointer',
                borderColor: colors.mediumSlateBlue,
                color: colors.mediumSlateBlue,
                '&:hover': disabled ? {} : {
                  backgroundColor: colors.mediumSlateBlue,
                  color: 'white',
                }
              }}
            />
          )}

          {/* Available options - now objects with name and icon */}
          {availableOptions.map((option) => {
            const optionName = typeof option === 'object' ? option.name : option;
            const optionIcon = typeof option === 'object' ? option.icon : '';
            const isSelected = locationPath[currentLevel] === optionName;
            
            return (
              <Chip
                key={optionName}
                label={optionIcon ? `${optionIcon} ${optionName}` : optionName}
                variant={isSelected ? 'filled' : 'outlined'}
                onClick={() => handleOptionSelect(option)}
                sx={{
                  cursor: disabled ? 'default' : 'pointer',
                  backgroundColor: isSelected ? colors.actionBlue : 'transparent',
                  color: isSelected ? 'white' : colors.darkSlateBlue,
                  borderColor: colors.actionBlue,
                  '&:hover': disabled ? {} : {
                    backgroundColor: colors.actionBlue,
                    color: 'white',
                  }
                }}
              />
            );
          })}

          {/* Contextual Custom option - only show if we have a context */}
          {currentLevel > 0 && (
            <Chip
              icon={<AddIcon />}
              label={`+ Add to ${currentLevel === 1 ? locationPath[0] : 
                     currentLevel === 2 ? locationPath[1] : 'Current'}`}
              variant="outlined"
              onClick={() => handleCustomLocation('contextual')}
              sx={{
                cursor: disabled ? 'default' : 'pointer',
                borderColor: colors.actionBlue,
                color: colors.actionBlue,
                fontSize: '0.75rem',
                '&:hover': disabled ? {} : {
                  backgroundColor: colors.actionBlue,
                  color: 'white',
                }
              }}
            />
          )}

          {/* Full Custom option */}
          <Chip
            icon={<AddIcon />}
            label="Custom Location..."
            variant="outlined"
            onClick={() => handleCustomLocation('full')}
            sx={{
              cursor: disabled ? 'default' : 'pointer',
              borderColor: colors.mediumSlateBlue,
              color: colors.mediumSlateBlue,
              '&:hover': disabled ? {} : {
                backgroundColor: colors.mediumSlateBlue,
                color: 'white',
              }
            }}
          />
        </Box>

        {/* Finish selection button for incomplete paths */}
        {locationPath.length > 0 && currentLevel === locationPath.length && (
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleFinishSelection}
              sx={{
                backgroundColor: colors.actionBlue,
                '&:hover': {
                  backgroundColor: colors.darkSlateBlue,
                }
              }}
            >
              Use "{buildLocationPath(locationPath)}"
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box>
      {/* Label */}
      <Typography variant="body2" color="text.primary" sx={{ mb: 1, fontWeight: 500 }}>
        {label}
      </Typography>

      {/* Current selection display */}
      {value && (
        <Box sx={{ mb: 2, p: 2, backgroundColor: colors.lightGray, borderRadius: 1 }}>
          <Typography variant="body2" color="text.primary">
            Selected: <strong>{value}</strong>
          </Typography>
        </Box>
      )}

      {/* Quick selections */}
      {renderQuickSelections()}

      {/* Breadcrumb navigation */}
      {renderBreadcrumb()}

      {/* Current level options */}
      <Fade in={true} timeout={300}>
        <Box>
          {renderCurrentLevelOptions()}
        </Box>
      </Fade>

      {/* Custom location dialog */}
      <Dialog open={isCustomDialogOpen} onClose={handleDialogClose}>
        <DialogTitle>
          {customDialogType === 'contextual' 
            ? `Add Custom Option to ${customDialogContext}`
            : 'Custom Location'
          }
        </DialogTitle>
        <DialogContent>
          {customDialogType === 'contextual' && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add a new option to <strong>{customDialogContext}</strong>. 
              This will create: <strong>{customDialogContext} - [Your Input]</strong>
            </Typography>
          )}
          <TextField
            autoFocus
            margin="dense"
            label={customDialogType === 'contextual' 
              ? `New option for ${customDialogContext}`
              : "Enter custom location"
            }
            fullWidth
            variant="outlined"
            value={customLocation}
            onChange={(e) => setCustomLocation(e.target.value)}
            placeholder={customDialogType === 'contextual' 
              ? "e.g., Closet, Storage Bench, etc."
              : "e.g., Bedroom - Master - Walk-in Closet"
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleCustomSubmit} variant="contained">
            {customDialogType === 'contextual' ? 'Add Option' : 'Add Location'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LocationHierarchySelector;
